import React, { useState } from 'react';
import { intl } from 'i18n';
import { Icon } from '../../../../elements';
import { useLayoutSize, WrappedMessage } from '../../../../utils';
import messages from '../../../../career-explorer/displayMessages';
import classNames from 'classnames';
import PulseCard from '../../PulseCard';
import VideoPlayer from '../../VideoPlayer/VideoPlayer';
import CareerPronunciation from './CareerPronunciation';

interface CareerIntroductionProps {
    description: string;
    imageUrl?: string;
    pronunciation: string;
    pronunciationAudio?: string;
    alternativeNames: string[];
    videoUrl?:string;
}

const CareerIntroduction: React.FC<CareerIntroductionProps> = ({
 description,
 imageUrl,
 pronunciation,
 pronunciationAudio,
 alternativeNames,
 videoUrl,
}) => {

    const [showAllNames, setShowAllNames] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);

    const isMobile = ['small', 'mobile'].includes(useLayoutSize());
    const isMobileOrTablet = ['small', 'mobile', 'medium'].includes(useLayoutSize());

    const handleShowMore = () => {
        setShowAllNames(!showAllNames);
    };

    const toggleAudio = () => {
        if (pronunciationAudio) {
            const audio = new Audio(pronunciationAudio);
            if (!isAudioPlaying) {
                audio.play();
                setIsAudioPlaying(true);
                audio.onended = () => setIsAudioPlaying(false);
            }
        }
    };

    const defaultCountToShow = 4;
    const displayedNames = (!isMobileOrTablet ? true : showAllNames)
        ? alternativeNames
        : alternativeNames?.slice(0, defaultCountToShow);

    const CareerIllustration = () => (
        <>
            {(imageUrl || videoUrl) &&
              <div className='image-container'>
                  {videoUrl ?
                      <VideoPlayer
                          videoUrl={videoUrl}
                          posterUrl={imageUrl}
                      />
                      :
                      <img
                          className='career-icon'
                          alt={intl.formatMessage(messages.altTextForIllustration)}
                          src={imageUrl}
                      />
                  }
              </div>
            }
        </>
        );

    return (
        <div className='career-intro'>
            <h2> <WrappedMessage message={messages.careerIntroSectionHeading} /></h2>
            <div className={classNames({'career-introduction-desktop': !isMobileOrTablet })}>
                <div className={classNames('career-introduction', {'text-content': !isMobileOrTablet})}>
                    <div className='career-introduction__description'>{description}</div>

                    {isMobileOrTablet &&
                      <CareerIllustration />
                    }
                    <CareerPronunciation
                        pronunciation={pronunciation}
                        pronunciationAudio={pronunciationAudio}
                        hasNotVideoOrImage={(!imageUrl && !videoUrl)}
                    />
                </div>

                {!isMobileOrTablet &&
                  <div className={classNames({'image-content': !isMobileOrTablet})}>
                    <CareerIllustration />
                  </div>
                }
            </div>
            {alternativeNames &&
                <div className='alternative-names'>
                    <h4 className='alternative-names-heading'>
                        <WrappedMessage message={messages.careerAlternativeNamesText} />
                    </h4>
                    <p>
                        {displayedNames && displayedNames.join(', ')}
                        {((alternativeNames.length > defaultCountToShow) && isMobileOrTablet) &&  (
                            <>
                                {showAllNames ? (
                                    <button className='expand-button' onClick={handleShowMore}>
                                        <WrappedMessage message={messages.showLessNamesText} />
                                    </button>
                                ) : (
                                    <button className='expand-button' onClick={handleShowMore}>
                                        <WrappedMessage
                                            message={messages.showAllNamesText}
                                            values={{count: alternativeNames.length - defaultCountToShow}}
                                        />
                                    </button>
                                )}
                            </>
                        )}
                    </p>
                </div>
            }
        </div>
    );
};

export default CareerIntroduction;
